import { useEffect, useState, useMemo } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Users, GitBranch, Search, ZoomIn, ZoomOut, RotateCcw,
  ChevronRight, ChevronDown, Info, User2, Building2, Mail, Phone,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";

// Simple avatar circle from initials
function Avatar({ nama, size = 40, hasPhoto = false, empId }) {
  const initial = (nama || "?").trim()[0]?.toUpperCase() || "?";
  const [photoUrl, setPhotoUrl] = useState(null);
  useEffect(() => {
    // Endpoint lama GET /employees/{id}/photo tidak pernah ada di backend — yang terdaftar
    // hanya POST (upload). Akibatnya setiap permintaan gagal diam-diam dan foto karyawan
    // TIDAK PERNAH tampil di Bagan Struktur. Endpoint yang benar-benar ada adalah
    // GET /employees/{id}/avatar (employee_docs.py) yang membalas byte gambar, bukan JSON —
    // jadi dipakai fetch + blob URL supaya token tetap di header Authorization.
    if (!hasPhoto || !empId) return;
    let batal = false;
    let url;
    const token = localStorage.getItem("hris_token");
    fetch(`${API}/employees/${empId}/avatar`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.blob() : null))
      .then(b => {
        if (batal || !b) return;
        url = URL.createObjectURL(b);
        setPhotoUrl(url);
      })
      .catch(() => {});
    return () => { batal = true; if (url) URL.revokeObjectURL(url); };
  }, [hasPhoto, empId]);
  return photoUrl ? (
    <img src={photoUrl} alt={nama}
      className="rounded-full object-cover shrink-0 border-2 border-white shadow-sm"
      style={{ width: size, height: size }} />
  ) : (
    <div
      className="rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-semibold shrink-0 shadow-sm"
      style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initial}
    </div>
  );
}

// Flatten the org tree into a linear list with depth+matched info.
// Iterative approach avoids babel-plugin infinite recursion on <OrgNode> self-reference.
function flattenOrgTree(nodes, depth, expandedSet, search, acc) {
  const q = (search || "").toLowerCase();
  for (const n of nodes) {
    const hasChildren = n.children && n.children.length > 0;
    const isExpanded = expandedSet.has(n.id);
    const matches = q && n.nama?.toLowerCase().includes(q);
    acc.push({ n, depth, hasChildren, isExpanded, matches });
    if (isExpanded && hasChildren) {
      flattenOrgTree(n.children, depth + 1, expandedSet, search, acc);
    }
  }
  return acc;
}

function OrgTreeList({ roots, expandedSet, toggle, onClickNode, search }) {
  const rows = flattenOrgTree(roots || [], 0, expandedSet, search, []);
  return (
    <>
      {rows.map(({ n, depth, hasChildren, isExpanded, matches }) => (
        <div key={n.id} style={{ paddingLeft: `${depth * 16}px` }}
          data-testid={`org-node-${n.id}`}
          className={`flex items-start gap-2 py-1.5 group ${matches ? "bg-amber-50 rounded-md" : ""} ${depth > 0 ? "border-l-2 border-emerald-200 ml-4 pl-2" : ""}`}>
          <button onClick={() => hasChildren && toggle(n.id)}
            className={`w-5 h-5 grid place-items-center rounded hover:bg-slate-200 shrink-0 ${!hasChildren ? "invisible" : ""}`}>
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onClickNode(n)}
            data-testid={`org-btn-${n.id}`}
            className="flex items-center gap-2 hover:bg-emerald-50 rounded-lg px-2 py-1 text-left flex-1 min-w-0 transition-colors">
            <Avatar nama={n.nama} size={36} hasPhoto={n.has_photo} empId={n.id} />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm text-slate-900 truncate">{n.nama}</p>
              <p className="text-[11px] text-slate-500 truncate">
                {n.branch || "—"}
                {n.branch && <span> · {n.branch}</span>}
                {hasChildren && <span className="ml-1 px-1.5 py-0 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-bold">{n.children.length} bawahan</span>}
              </p>
            </div>
          </button>
        </div>
      ))}
    </>
  );
}

export default function OrgChart() {
  const { user } = useAuth();
  const [tree, setTree] = useState(null);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/org-chart")
      .then(r => {
        setTree(r.data);
        // Auto-expand roots
        setExpanded(new Set(r.data.roots.map(x => x.id)));
      })
      .catch(e => toast.error("Gagal memuat bagan"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setExpanded(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const expandAll = () => {
    const all = new Set();
    const walk = (n) => { all.add(n.id); (n.children || []).forEach(walk); };
    (tree?.roots || []).forEach(walk);
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set((tree?.roots || []).map(r => r.id)));

  const onClickNode = async (node) => {
    setSelected(node);
    try {
      const { data } = await api.get(`/employees/${node.id}/subordinates?deep=true`);
      setSubs(data);
    } catch { setSubs([]); }
  };

  // Search across all nodes flattened
  const matchIds = useMemo(() => {
    if (!search || !tree) return new Set();
    const found = new Set();
    const walk = (n) => {
      if (n.nama?.toLowerCase().includes(search.toLowerCase())) found.add(n.id);
      (n.children || []).forEach(walk);
    };
    (tree.roots || []).forEach(walk);
    return found;
  }, [search, tree]);
  // Auto-expand ancestors of matches
  useEffect(() => {
    if (!search || !tree || matchIds.size === 0) return;
    const parents = new Map();
    const walk = (n, parent = null) => {
      parents.set(n.id, parent);
      (n.children || []).forEach(c => walk(c, n.id));
    };
    (tree.roots || []).forEach(r => walk(r));
    setExpanded(current => {
      const toExpand = new Set(current);
      matchIds.forEach(id => {
        let cur = parents.get(id);
        while (cur) { toExpand.add(cur); cur = parents.get(cur); }
      });
      return toExpand;
    });
  }, [search, matchIds, tree]);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Bagan Struktur</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <GitBranch className="w-6 h-6 text-emerald-600" /> Organisasi & Reporting Line
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1" />
          Plug-and-play — atur <b>atasan</b> di form karyawan → langsung tercermin di sini.
          Supervisor otomatis dapat izin approval (cuti/telat/lembur) untuk seluruh bawahan &amp; cabang bawahannya.
        </p>
      </header>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            data-testid="org-search"
            placeholder="Cari nama karyawan..."
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500/40" />
        </div>
        <button onClick={expandAll} data-testid="btn-expand-all"
          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium">
          <ZoomIn className="w-3.5 h-3.5" /> Buka Semua
        </button>
        <button onClick={collapseAll} data-testid="btn-collapse-all"
          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium">
          <ZoomOut className="w-3.5 h-3.5" /> Tutup Semua
        </button>
        {tree && (
          <span className="text-xs text-slate-500 ml-auto">
            {tree.total_employees} karyawan · {tree.roots?.length || 0} tanpa atasan
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 min-h-[500px]" data-testid="org-tree">
          <LoadingState
            loading={loading}
            kosong={!loading && (!tree || tree.roots.length === 0)}
            pesanKosong="Belum ada data karyawan."
            data-testid="org-tree-loading"
          >
            {tree && (
              <OrgTreeList roots={tree.roots} expandedSet={expanded} toggle={toggle}
                onClickNode={onClickNode} search={search} />
            )}
          </LoadingState>
        </div>

        {/* Detail panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 h-fit sticky top-4" data-testid="org-detail">
          {!selected ? (
            <div className="text-center py-6">
              <User2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-400">Klik karyawan untuk lihat detail &amp; bawahan</p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-4">
                <Avatar nama={selected.nama} size={56} hasPhoto={selected.has_photo} empId={selected.id} />
                <div className="min-w-0 flex-1">
                  <p className="font-display font-semibold text-slate-900">{selected.nama}</p>
                  <p className="text-xs text-slate-500">{selected.branch || "—"}</p>
                </div>
              </div>
              <div className="space-y-1.5 text-xs">
                {selected.branch && <p className="flex items-center gap-2 text-slate-700"><Building2 className="w-3.5 h-3.5 text-slate-400" /> {selected.branch}</p>}
                {selected.email && <p className="flex items-center gap-2 text-slate-700"><Mail className="w-3.5 h-3.5 text-slate-400" /> {selected.email}</p>}
                {selected.no_hp && <p className="flex items-center gap-2 text-slate-700"><Phone className="w-3.5 h-3.5 text-slate-400" /> {selected.no_hp}</p>}
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-2">
                  Bawahan Langsung + Cabang ({subs.length})
                </p>
                {subs.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Tidak punya bawahan.</p>
                ) : (
                  <div className="space-y-1 max-h-[320px] overflow-y-auto">
                    {subs.map(s => (
                      <div key={s.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50">
                        <Avatar nama={s.nama} size={26} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">{s.nama}</p>
                          <p className="text-[10px] text-slate-500 truncate">{s.branch || ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100">
                <p className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                  ✓ Sebagai supervisor, otomatis punya hak approve cuti untuk seluruh bawahan langsung + cabang.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
